import axios, { isAxiosError } from 'axios';
import { useCookie } from '@/composable/useCookie';
import { setImplicitTokenInCookie } from '@/composable/useTracking';
import { settings } from '@/settings/DynamicSettings';
import {
  DesignDownloadTracking,
  PageViewTracking,
  ProductClickTracking,
  ProductImageDownloadTracking,
  ProductLinkClickTracking,
  TimeOnPageTracking,
} from '../tracking-definitions';
import { ImplicitTokenResponse, useAuthService } from './useAuthService';

export default function useTrackingService() {
  const authService = useAuthService();
  const implicitTokenCookie =
    useCookie<ImplicitTokenResponse>('implicit-token');
  function getTrackerRequestConfig() {
    return {
      headers: {
        authorization: implicitTokenCookie.get()?.implicit_token ?? '',
      },
    };
  }

  function sendProductClickEvent(event: ProductClickTracking) {
    return axios
      .put(settings.TRACKING_URL, event, getTrackerRequestConfig())
      .catch(reloadImplicitToken);
  }

  function sendPageViewEvent(event: PageViewTracking) {
    return axios
      .put(settings.TRACKING_URL, event, getTrackerRequestConfig())
      .catch(reloadImplicitToken);
  }

  function sendTimeOnPageEvent(
    event: TimeOnPageTracking,
    options?: { isKeepAliveRequest: boolean }
  ) {
    if (!options?.isKeepAliveRequest) {
      return axios
        .put(settings.TRACKING_URL, event, getTrackerRequestConfig())
        .catch(reloadImplicitToken);
    } else {
      const headers = new Headers({
        'Content-Type': 'application/json',
        authorization: getTrackerRequestConfig().headers.authorization,
      });

      fetch(settings.TRACKING_URL, {
        method: 'PUT',
        body: JSON.stringify(event),
        keepalive: true,
        headers,
      });
    }
  }

  function sendDesignDownloadEvent(event: DesignDownloadTracking) {
    return axios
      .put(settings.TRACKING_URL, event, getTrackerRequestConfig())
      .catch(reloadImplicitToken);
  }

  function sendProductLinkClickEvent(event: ProductLinkClickTracking) {
    return axios
      .put(settings.TRACKING_URL, event, getTrackerRequestConfig())
      .catch(reloadImplicitToken);
  }

  function sendProductImageDownloadEvent(event: ProductImageDownloadTracking) {
    return axios
      .put(settings.TRACKING_URL, event, getTrackerRequestConfig())
      .catch(reloadImplicitToken);
  }

  async function reloadImplicitToken(err: unknown) {
    if (
      isAxiosError(err) &&
      err.response?.status &&
      [401, 403].includes(err.response?.status)
    ) {
      const response = await authService.requestImplicitToken();
      setImplicitTokenInCookie(response.data);
    }
  }

  return {
    sendProductClickEvent,
    sendPageViewEvent,
    sendTimeOnPageEvent,
    sendDesignDownloadEvent,
    sendProductLinkClickEvent,
    sendProductImageDownloadEvent,
  };
}
